import { useEffect, useState } from "react";
import { Download, X } from "lucide-react";
import { SaraLogo } from "./SaraLogo";

const DISMISS_KEY = "sara-pwa-dismissed";

function isStandalone() {
  return window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;
}
function isIos() {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent) && !window.MSStream;
}

// "Installer Sara Connor" prompt — uses the native beforeinstallprompt where
// available, and a manual hint on iOS Safari (which has no such event).
export function PWAInstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;
    if (localStorage.getItem(DISMISS_KEY)) return;

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    const onInstalled = () => {
      setVisible(false);
      localStorage.setItem(DISMISS_KEY, "installed");
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);

    let t;
    if (isIos()) {
      t = setTimeout(() => {
        setIosHint(true);
        setVisible(true);
      }, 2500);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
      if (t) clearTimeout(t);
    };
  }, []);

  if (!visible) return null;

  const dismiss = () => {
    setVisible(false);
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  };
  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try { await deferred.userChoice; } catch (_) {}
    setDeferred(null);
    setVisible(false);
    localStorage.setItem(DISMISS_KEY, "prompted");
  };

  return (
    <div
      data-testid="pwa-install-prompt"
      className="fixed left-1/2 -translate-x-1/2 bottom-[84px] md:bottom-6 z-[80] w-[calc(100%-2rem)] max-w-sm glass-card rounded-2xl p-3.5 shadow-[0_0_40px_rgba(0,240,255,0.18)] animate-rise"
    >
      <div className="flex items-center gap-3">
        <div className="shrink-0 h-11 w-11 rounded-xl flex items-center justify-center bg-cyan-500/10 border border-cyan-500/25">
          <SaraLogo className="h-7 w-7" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-white">Installer Sara Connor</div>
          <div className="text-[11px] text-slate-400 leading-snug">
            {iosHint
              ? "Appuyez sur Partager puis « Sur l'écran d'accueil »."
              : "Accès plein écran, hors-ligne, comme une vraie application."}
          </div>
        </div>
        <button
          data-testid="pwa-dismiss"
          onClick={dismiss}
          aria-label="Plus tard"
          className="shrink-0 h-8 w-8 flex items-center justify-center rounded-full text-slate-400 hover:text-white"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      {!iosHint && (
        <button
          data-testid="pwa-install-button"
          onClick={install}
          className="mt-3 w-full py-2.5 rounded-xl flex items-center justify-center gap-2 text-sm font-semibold text-[#061427] bg-gradient-to-r from-cyan-400 to-blue-500 shadow-[0_0_18px_rgba(0,240,255,0.4)] hover:brightness-110 transition"
        >
          <Download className="h-4 w-4" /> Installer l'application
        </button>
      )}
    </div>
  );
}

export default PWAInstallPrompt;
